import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CommunicationTemplateService, CommunicationTemplate } from '../../services/communication-template.service';
import { AuthService } from '../../auth/auth.service';

@Component({
  selector: 'app-template-quick-access',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './template-quick-access.component.html',
  styleUrls: ['./template-quick-access.component.css']
})
export class TemplateQuickAccessComponent implements OnInit, AfterViewInit {
  @Input() channelType: string = '';
  @Output() templateSelected = new EventEmitter<CommunicationTemplate>();
  @Output() cancel = new EventEmitter<void>();
  
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  templates: CommunicationTemplate[] = [];
  recentTemplates: CommunicationTemplate[] = [];
  displayTemplates: CommunicationTemplate[] = [];
  searchQuery: string = '';
  selectedTemplate: CommunicationTemplate | null = null;
  isLoading: boolean = false;

  constructor(
    private smsTemplateService: CommunicationTemplateService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.loadTemplates();
  }

  ngAfterViewInit() {
    // Focus the search input when modal opens
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.nativeElement.focus();
      }
    }, 100);
  }

  loadTemplates() {
    this.isLoading = true;
    this.smsTemplateService.getTemplates().subscribe({
      next: (response) => {
        if (response.success) {
          let templates = Array.isArray(response.data) ? response.data : [];

          // For RC users, only show their own company templates (filter out MC templates)
          if (this.isRCUser()) {
            const currentUser = this.authService.currentUserValue;
            templates = templates.filter(template =>
              template.company_id === currentUser?.company_id
            );
          }

          // Filter by channelType if provided (e.g. 'sms', 'email', 'whatsapp')
          if (this.channelType) {
            const typeFilter = this.channelType.toLowerCase();
            templates = templates.filter(t => t.template_type?.toLowerCase() === typeFilter);
          }

          this.templates = templates;
          this.setupTemplateDisplay();
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to load templates:', error);
        this.isLoading = false;
      }
    });
  }

  setupTemplateDisplay() {
    // Sort templates by usage and last used date for recent templates
    this.recentTemplates = this.templates
      .filter(t => t.usage_count > 0 && t.last_used_at)
      .sort((a, b) => {
        const aDate = new Date(a.last_used_at!).getTime();
        const bDate = new Date(b.last_used_at!).getTime();
        return bDate - aDate;
      })
      .slice(0, 3); // Show top 3 recent templates

    this.updateDisplayTemplates();
  }

  updateDisplayTemplates() {
    if (!this.searchQuery.trim()) {
      // Show recent templates first, then all templates
      const recentIds = this.recentTemplates.map(t => t._id);
      const otherTemplates = this.templates
        .filter(t => !recentIds.includes(t._id))
        .sort((a, b) => a.name.localeCompare(b.name));
      
      this.displayTemplates = [...this.recentTemplates, ...otherTemplates];
    } else {
      // Filter templates based on search query
      const query = this.searchQuery.toLowerCase();
      this.displayTemplates = this.templates
        .filter(template => 
          template.name.toLowerCase().includes(query) ||
          template.body.toLowerCase().includes(query)
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  onSearch() {
    this.updateDisplayTemplates();
  }

  selectTemplate(template: CommunicationTemplate) {
    this.selectedTemplate = template;
    
    // Track template usage
    this.smsTemplateService.useTemplate(template._id).subscribe({
      next: () => {
        // Update local usage count
        template.usage_count++;
        template.last_used_at = new Date();
      },
      error: (error) => {
        console.error('Failed to track template usage:', error);
      }
    });

    // Emit the selected template
    this.templateSelected.emit(template);
  }

  onCancel() {
    this.cancel.emit();
  }

  getChannelBadgeClass(templateType: string): string {
    switch (templateType) {
      case 'sms': return 'channel-badge sms';
      case 'email': return 'channel-badge email';
      case 'whatsapp': return 'channel-badge whatsapp';
      default: return 'channel-badge';
    }
  }

  getChannelDisplayName(templateType: string): string {
    switch (templateType) {
      case 'sms': return 'SMS';
      case 'email': return 'EMAIL';
      case 'whatsapp': return 'WHATSAPP';
      default: return templateType.toUpperCase();
    }
  }

  isRCUser(): boolean {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.company_type === 'RC';
  }
}